/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce, isChannelTypeB2B, IImageSettings } from '@msdyn365-commerce/core';
import { AsyncResult, Customer, ProductDimension, ProductList, ProductPrice, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import {
    addItemToOrderTemplate,
    AddItemToOrderTemplateInput,
    getPriceForSelectedVariant,
    OrderTemplate,
    PriceForSelectedVariantInput,
    updateOrderTemplateAction,
    UpdateOrderTemplateInput,
    validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
import { Alert, UncontrolledTooltip } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as Popper from 'popper.js';
import React, { useState } from 'react';

import { AddToOrderTemplateDialog, IAddToOrderTemplateDialogData, IAddToOrderTemplateDialogResources, DuplicateItemsWhenAddingToOrderTemplateDialog, IDuplicateItemsWhenAddingToOrderTemplateDialogResources, IItemAddedToOrderTemplateDialogResources, ItemAddedToOrderTemplateDialog, IOrderTemplateNameDialogResources } from '@msdyn365-commerce/components';

export interface IAddToOrderTemplateComponentProps extends IComponentProps<IAddToOrderTemplateData> {
    className?: string;

    addToOrderTemplateDialogResources: IAddToOrderTemplateDialogResources;
    createOrderTemplateDialogResources: IOrderTemplateNameDialogResources;
    itemAddedToOrderTemplateDialogResources: IItemAddedToOrderTemplateDialogResources;
    duplicateItemsWhenAddingToOrderTemplateDialogResources: IDuplicateItemsWhenAddingToOrderTemplateDialogResources;

    addToOrderTemplateButtonText: string;
    addToOrderTemplateButtonTooltip: string;

    addToOrderTemplateMessage?: string;
    addItemToOrderTemplateError?: string;

    showButtonText?: boolean;
    showButtonTooltip?: boolean;
    shouldShowButtonFailedTooltip?: boolean;
    disableButton?: boolean;
    tooltipPosition?: Popper.Placement;
    showStatusMessage?: boolean;
    disabled?: boolean;
    canAddMasterProduct?: boolean;
    isMultipleCatalogFeatureEnabledInHq?: boolean;
    catalogId?: number;

    imageSettings?: IImageSettings;

    onSuccess?(): void;
    onError?(result: IOrderTemplateActionErrorResult): void;
}

export interface IOrderTemplateActionErrorResult {
    status: 'FAILED' | 'MISSING_DIMENSION';

    missingDimensions?: ProductDimension[];
}

interface IAddToOrderTemplateStateManager {
    waiting: boolean;
    content: IAddToOrderTemplateContentState;

    setWaiting(newWaiting: boolean): void;
    setContent(newContent: IAddToOrderTemplateContentState): void;
}

interface IAddToOrderTemplateContentState {
    showAlert?: boolean;
    orderTemplateTextMessage?: string;
    color?: string;
}

interface IAddToOrderTemplateData {
    orderTemplates: OrderTemplate[];

    product: SimpleProduct;
    quantity: number;

    customerInformation?: Customer;
    isChannelTypeB2B?: boolean;
}

export interface IAddToOrderTemplateComponent extends IComponent<IAddToOrderTemplateComponentProps> {
    onClick(): (
        event: React.MouseEvent<HTMLElement>,
        props: IAddToOrderTemplateComponentProps,
        state: IAddToOrderTemplateStateManager
    ) => void;
}

const onClick = async (
    _event: React.MouseEvent<HTMLElement>,
    props: IAddToOrderTemplateComponentProps,
    state: IAddToOrderTemplateStateManager,
    openModal: (opened: boolean) => void
): Promise<void> => {
    const { user } = props.context.request;

    if (state.waiting) {
        return;
    }

    if (!user.isAuthenticated && user.signInUrl && window) {
        window.location.assign(user.signInUrl);
        return;
    }

    addToOrderTemplate(props, openModal);
    state.setWaiting(false);
};

const onDismiss = (state: IAddToOrderTemplateStateManager): void => {
    state.setContent({ color: 'success', showAlert: false });
};

const AddToOrderTemplateComponentActions = {
    onClick,
    onDismiss
};

const hasProductInOrderTemplates = (productToFind: SimpleProduct) => {
    /*
    TODO: Currently API does not support filtering by products.
    await getProductListLinesAsync(context, '',)
    return orderTemplates.filter(orderTemplate =>
        orderTemplate.orderTemplateLines.filter(
            product => product.productListLine.ProductId === productToFind.RecordId).length !== 0).length !== 0;
    */
    return false;
};

const AddToOrderTemplate: React.FC<IAddToOrderTemplateComponentProps> = (props: IAddToOrderTemplateComponentProps) => {
    if (!isChannelTypeB2B(props.context.actionContext.requestContext)) {
        return null;
    }

    const [waiting, setWaiting] = useState(false);
    const [content, setContent] = useState({});
    const state: IAddToOrderTemplateStateManager = {
        waiting,
        content,

        setWaiting,
        setContent
    };

    const [addToOrderTemplateModalOpen, setAddToOrderTemplateModalOpen] = useState(false);

    const [chosenOrderTemplateId, setChosenOrderTemplateId] = useState<string | undefined>(undefined);
    const [productPrice, setProductPrice] = useState<ProductPrice | null>(null);
    const [itemAddedToOrderTemplateDialogOpen, setItemAddedToOrderTemplateDialogOpen] = useState(false);
    const [duplicateItemsWhenAddingToOrderTemplateDialogOpen, setDuplicateItemsWhenAddingToOrderTemplateDialogOpen] = useState(false);
    const catalogId = props.catalogId;
    validateCatalogId(catalogId);
    const isMultipleCatalogFeatureEnabledInHq = props.isMultipleCatalogFeatureEnabledInHq || false;
    const executeAddItemToOrderTemplate = async (orderTemplate: ProductList) => {
        setChosenOrderTemplateId(orderTemplate.Id);
        const input = isMultipleCatalogFeatureEnabledInHq
            ? new AddItemToOrderTemplateInput(
                  orderTemplate.Id,
                  props.data.product.RecordId,
                  props.data.quantity,
                  props.data.product.DefaultUnitOfMeasure || 'ea',
                  isMultipleCatalogFeatureEnabledInHq,
                  catalogId
              )
            : new AddItemToOrderTemplateInput(
                  orderTemplate.Id,
                  props.data.product.RecordId,
                  props.data.quantity,
                  props.data.product.DefaultUnitOfMeasure || 'ea'
              );
        await addItemToOrderTemplate(input, props.context.actionContext)
            .then(async result => {
                const retrievedProductPrice = await getPriceForSelectedVariant(
                    new PriceForSelectedVariantInput(
                        props.data.product.RecordId,
                        props.context.actionContext.requestContext.apiSettings.channelId,
                        props.data.product
                    ),
                    props.context.actionContext
                );
                setProductPrice(retrievedProductPrice);
                setAddToOrderTemplateModalOpen(false);
                setTimeout(() => {
                    setItemAddedToOrderTemplateDialogOpen(true);
                }, 500);
            })
            .catch(error => {
                if (error.error?.name !== 'Microsoft_Dynamics_Commerce_Runtime_DuplicateObject') {
                    return AsyncResult.reject(error);
                }
                setAddToOrderTemplateModalOpen(false);
                setTimeout(() => {
                    setDuplicateItemsWhenAddingToOrderTemplateDialogOpen(true);
                }, 500);
                return AsyncResult.resolve();
            });
    };

    const onDuplicateDialogCancel = () => {
        setDuplicateItemsWhenAddingToOrderTemplateDialogOpen(false);
        setTimeout(() => {
            setAddToOrderTemplateModalOpen(true);
        }, 500);
    };

    const overrideQuantity = async () => {
        const input = isMultipleCatalogFeatureEnabledInHq
            ? new UpdateOrderTemplateInput(
                  chosenOrderTemplateId!,
                  props.data.product.RecordId,
                  props.data.quantity,
                  props.data.product.DefaultUnitOfMeasure || 'ea',
                  isMultipleCatalogFeatureEnabledInHq,
                  catalogId
              )
            : new UpdateOrderTemplateInput(
                  chosenOrderTemplateId!,
                  props.data.product.RecordId,
                  props.data.quantity,
                  props.data.product.DefaultUnitOfMeasure || 'ea'
              );
        await updateOrderTemplateAction(input, props.context.actionContext);

        const retrievedProductPrice = await getPriceForSelectedVariant(
            new PriceForSelectedVariantInput(
                props.data.product.RecordId,
                props.context.actionContext.requestContext.apiSettings.channelId,
                props.data.product
            ),
            props.context.actionContext
        );
        setProductPrice(retrievedProductPrice);
        setDuplicateItemsWhenAddingToOrderTemplateDialogOpen(false);
        setTimeout(() => {
            setItemAddedToOrderTemplateDialogOpen(true);
        }, 500);
    };

    const {
        addToOrderTemplateButtonText,
        showStatusMessage,
        showButtonText,
        className,
        disabled,
        showButtonTooltip,
        shouldShowButtonFailedTooltip,
        disableButton,
        tooltipPosition,
        addToOrderTemplateButtonTooltip,
        addToOrderTemplateDialogResources,
        createOrderTemplateDialogResources
    } = props;

    const onDismissHandler = () => {
        AddToOrderTemplateComponentActions.onDismiss(state);
    };
    const ariaLabel = addToOrderTemplateButtonText;
    const addToOrderTemplateRef: React.RefObject<HTMLButtonElement> = React.createRef();
    const showMessage = showStatusMessage !== undefined ? showStatusMessage : true;
    const showTooltip = showButtonTooltip !== undefined ? showButtonTooltip : false;
    const tooltipPos = showButtonTooltip !== undefined ? tooltipPosition : 'top-end';

    const dialogData: IAddToOrderTemplateDialogData = {
        orderTemplates: props.data.orderTemplates
    };

    const onClickHandler = (event: React.MouseEvent<HTMLElement>) => {
        return AddToOrderTemplateComponentActions.onClick(event, props, state, setAddToOrderTemplateModalOpen);
    };

    return (
        <>
            <>
                <button
                    className={classnames(
                        className,
                        hasProductInOrderTemplates(props.data.product)
                            ? 'msc-add-to-order-template-icon__added'
                            : 'msc-add-to-order-template-icon',
                        state.waiting ? 'msc-add-to-order-template-icon__waiting' : ''
                    )}
                    aria-label={ariaLabel}
                    onClick={disableButton ? undefined : onClickHandler}
                    disabled={disabled}
                    ref={addToOrderTemplateRef}
                >
                    {showButtonText ? ariaLabel : 'Add to order template'}
                </button>
                {showTooltip && (
                    <UncontrolledTooltip placement={tooltipPos} trigger='hover focus' target={addToOrderTemplateRef}>
                        {shouldShowButtonFailedTooltip ? addToOrderTemplateButtonTooltip : ariaLabel}
                    </UncontrolledTooltip>
                )}
            </>
            {state.content.showAlert && showMessage ? (
                <div className='ms-order-template-items__product-status'>
                    <Alert color={state.content.color} isOpen={state.content.showAlert} toggle={onDismissHandler}>
                        {state.content.orderTemplateTextMessage}
                    </Alert>
                </div>
            ) : (
                ''
            )}
            <AddToOrderTemplateDialog
                className='msc-add-to-order-template-dialog'
                modalOpen={addToOrderTemplateModalOpen}
                setModalOpen={setAddToOrderTemplateModalOpen}
                context={props.context}
                id={props.id}
                typeName={props.typeName}
                data={dialogData}
                addToOrderTemplateDialogResources={addToOrderTemplateDialogResources}
                createOrderTemplateDialogResources={createOrderTemplateDialogResources}
                onOrderTemplateSelected={executeAddItemToOrderTemplate}
                isMultipleCatalogFeatureEnabledInHq={isMultipleCatalogFeatureEnabledInHq}
                {...(isMultipleCatalogFeatureEnabledInHq && { catalogId: props.catalogId })}
            />
            {chosenOrderTemplateId && productPrice && (
                <ItemAddedToOrderTemplateDialog
                    className='msc-item-added-to-order-template-dialog'
                    id={props.id}
                    typeName={props.typeName}
                    context={props.context}
                    dialogStrings={props.itemAddedToOrderTemplateDialogResources}
                    imageSettings={props.imageSettings !== undefined ? props.imageSettings : undefined}
                    data={{
                        product: props.data.product,
                        quantity: props.data.quantity,
                        orderTemplateId: chosenOrderTemplateId,
                        productPrice
                    }}
                    modalOpen={itemAddedToOrderTemplateDialogOpen}
                    isMultipleCatalogFeatureEnabledInHq={isMultipleCatalogFeatureEnabledInHq}
                    {...(isMultipleCatalogFeatureEnabledInHq && { catalogId: props.catalogId })}
                />
            )}
            <DuplicateItemsWhenAddingToOrderTemplateDialog
                className='msc-duplicate-items-when-adding-to-order-template-dialog'
                id={props.id}
                typeName={props.typeName}
                context={props.context}
                dialogStrings={props.duplicateItemsWhenAddingToOrderTemplateDialogResources}
                data={{}}
                modalOpen={duplicateItemsWhenAddingToOrderTemplateDialogOpen}
                executeOverrideQuantity={overrideQuantity}
                onCancel={onDuplicateDialogCancel}
                // isMultipleCatalogFeatureEnabledInHq={props.isMultipleCatalogFeatureEnabledInHq}
                // {...(props.isMultipleCatalogFeatureEnabledInHq && { catalogId: props.catalogId })}
            />
        </>
    );
};

const addToOrderTemplate = (props: IAddToOrderTemplateComponentProps, openModal: (opened: boolean) => void): void => {
    const { canAddMasterProduct } = props;

    if (props.data.product.Dimensions && !canAddMasterProduct) {
        const missingDimensions = props.data.product.Dimensions.filter(
            dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value)
        );

        if (missingDimensions.length > 0) {
            // At least one dimension with no value exists on the product, won't be able to proceed.
            propagateError(props, { status: 'MISSING_DIMENSION', missingDimensions });
            return;
        }
    }

    openModal(true);
};

const propagateError = (props: IAddToOrderTemplateComponentProps, result: IOrderTemplateActionErrorResult): void => {
    const { onError } = props;

    if (onError) {
        onError(result);
    }
};

export const AddToOrderTemplateComponent: React.FunctionComponent<IAddToOrderTemplateComponentProps> = msdyn365Commerce.createComponentOverride<
    // @ts-expect-error
    IAddToOrderTemplateComponent
>('AddToOrderTemplate', { component: AddToOrderTemplate, ...AddToOrderTemplateComponentActions });


export default AddToOrderTemplateComponent;